import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { useAuthStore } from '@/stores/authStore';

import { Landing } from '@/pages/Landing';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { CoachLayout } from '@/components/layouts/CoachLayout';
import { AtletaLayout } from '@/components/layouts/AtletaLayout';

const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const AtletaRegister = lazy(() => import('@/pages/auth/AtletaRegister'));
const CoachRegister = lazy(() => import('@/pages/auth/CoachRegister'));
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'));
const CompleteProfile = lazy(() => import('@/pages/auth/CompleteProfile'));
const JoinTeam = lazy(() => import('@/pages/auth/JoinTeam'));
const CoachTeamSetup = lazy(() => import('@/pages/auth/CoachTeamSetup'));
const CoachAthleteSetup = lazy(() => import('@/pages/auth/CoachAthleteSetup'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));

// COACH: pagine ricostruite con nuovo metodo (vedi FE-MAP.md).
// Placeholder unico WorkInProgress per le route non ancora rifatte.
const CoachHome = lazy(() => import('@/pages/coach/CoachHome'));
const AtletiLista = lazy(() => import('@/pages/coach/AtletiLista'));
const AtletaDettaglio = lazy(() => import('@/pages/coach/AtletaDettaglio'));
const AtletaStoricoFeedback = lazy(() => import('@/pages/coach/AtletaStoricoFeedback'));
const AtletaNuovo = lazy(() => import('@/pages/coach/AtletaNuovo'));
const AllenamentiLista = lazy(() => import('@/pages/coach/AllenamentiLista'));
const AllenamentoDettaglio = lazy(() => import('@/pages/coach/AllenamentoDettaglio'));
const AllenamentoBuilder = lazy(() => import('@/pages/coach/AllenamentoBuilder'));
const Gare = lazy(() => import('@/pages/coach/Gare'));
const GaraDettaglio = lazy(() => import('@/pages/coach/GaraDettaglio'));
const GaraLive = lazy(() => import('@/pages/coach/GaraLive'));
const CoachGaraRisultato = lazy(() => import('@/pages/coach/GaraRisultato'));
const CoachAltro = lazy(() => import('@/pages/coach/CoachAltro'));
const CoachTeam = lazy(() => import('@/pages/coach/altro/CoachTeam'));
const CoachLeaderboard = lazy(() => import('@/pages/coach/altro/CoachLeaderboard'));
const CoachGraduatorie = lazy(() => import('@/pages/coach/altro/CoachGraduatorie'));
const CoachTempiLimite = lazy(() => import('@/pages/coach/altro/CoachTempiLimite'));
const CoachRecordItaliani = lazy(() => import('@/pages/coach/altro/CoachRecordItaliani'));
const CoachRecordSocietari = lazy(() => import('@/pages/coach/altro/CoachRecordSocietari'));
const CoachNotifiche = lazy(() => import('@/pages/coach/altro/CoachNotifiche'));
const CoachProfilo = lazy(() => import('@/pages/coach/altro/CoachProfilo'));
const CoachConfronto = lazy(() => import('@/pages/coach/altro/CoachConfronto'));
const CoachWIP = lazy(() => import('@/pages/coach/WorkInProgress'));

// PRD 36 DA-PAGE-2=a: route atleta record-societari → riusa CoachRecordSocietari readonly.
const RecordSocietari = lazy(() => import('@/pages/coach/altro/CoachRecordSocietari'));

const AthleteHome = lazy(() => import('@/pages/athlete/AthleteHome'));
const AtletaAllenamentiLista = lazy(() => import('@/pages/athlete/AllenamentiLista'));
const AtletaAllenamentoDettaglio = lazy(() => import('@/pages/athlete/AllenamentoDettaglio'));
const AtletaGare = lazy(() => import('@/pages/athlete/Gare'));
const AtletaAnalisi = lazy(() => import('@/pages/athlete/Analisi'));
// Dettaglio gara: pagina condivisa col coach (stesse logiche; per l'atleta sono
// nascoste solo le azioni di gestione — upload PDF, tempi live, conflitti).
const AtletaGaraDettaglio = lazy(() => import('@/pages/coach/GaraDettaglio'));
const AtletaRisultatoDettaglio = lazy(() => import('@/pages/athlete/RisultatoDettaglio'));
const Squadra = lazy(() => import('@/pages/athlete/Squadra'));
const AtletaSquadraDettaglio = lazy(() => import('@/pages/athlete/AtletaSquadraDettaglio'));
const SquadraGara = lazy(() => import('@/pages/athlete/SquadraGara'));
const AtletaLeaderboard = lazy(() => import('@/pages/athlete/AtletaLeaderboard'));
const AtletaAltro = lazy(() => import('@/pages/athlete/AtletaAltro'));
// PRD 36 (DA-PAGE-0=SHARED): le 3 pagine consultazione FIN read-only riusano i component coach.
const AtletaGraduatorie = lazy(() => import('@/pages/coach/altro/CoachGraduatorie'));
const AtletaTempiLimite = lazy(() => import('@/pages/coach/altro/CoachTempiLimite'));
const RecordItaliani = lazy(() => import('@/pages/coach/altro/CoachRecordItaliani'));
const AtletaNotifiche = lazy(() => import('@/pages/athlete/AtletaNotifiche'));
const AtletaProfilo = lazy(() => import('@/pages/athlete/AtletaProfilo'));

function RootRedirect() {
    const role = useAuthStore(s => s.role);
    return <Navigate to={role === 'coach' ? '/coach' : '/atleta'} replace />;
}

function RedirectSquadraAtleta() {
    const { id } = useParams();
    return <Navigate to={`/atleta/altro/squadra/atleta/${id}`} replace />;
}

function RedirectSquadraGara() {
    const { id } = useParams();
    return <Navigate to={`/atleta/altro/squadra/gara/${id}`} replace />;
}

export function AppRouter() {
    return (
        <BrowserRouter>
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/home" element={<RootRedirect />} />

                    <Route element={<AuthLayout />}>
                        <Route path="/auth/login" element={<Login />} />
                        <Route path="/auth/register" element={<Register />} />
                        <Route path="/auth/register/atleta" element={<AtletaRegister />} />
                        <Route path="/auth/register/coach" element={<CoachRegister />} />
                        <Route path="/auth/verify-email" element={<VerifyEmail />} />
                        <Route path="/auth/complete-profile" element={<CompleteProfile />} />
                        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                        <Route path="/auth/reset-password" element={<ResetPassword />} />
                        <Route path="/onboarding/join-team" element={<JoinTeam />} />
                        <Route path="/onboarding/coach-team" element={<CoachTeamSetup />} />
                        <Route path="/onboarding/coach-athletes" element={<CoachAthleteSetup />} />
                        <Route path="/join/:code" element={<JoinTeam />} />
                    </Route>

                    <Route element={<ProtectedRoute role="coach" />}>
                        <Route path="/coach" element={<CoachLayout />}>
                            <Route index element={<CoachHome />} />
                            <Route path="atleti" element={<AtletiLista />} />
                            <Route path="atleti/nuovo" element={<AtletaNuovo />} />
                            <Route
                                path="atleti/manuale"
                                element={<CoachWIP title="Atleta manuale (disponibile prossimamente)" />}
                            />
                            <Route path="atleti/:id" element={<AtletaDettaglio />} />
                            <Route path="atleti/:id/storico-feedback" element={<AtletaStoricoFeedback />} />
                            <Route path="allenamenti" element={<AllenamentiLista />} />
                            <Route path="allenamenti/nuovo" element={<AllenamentoBuilder />} />
                            <Route path="allenamenti/:id/modifica" element={<AllenamentoBuilder />} />
                            <Route path="allenamenti/:id" element={<AllenamentoDettaglio />} />
                            <Route path="gare" element={<Gare />} />
                            <Route path="gare/:id" element={<GaraDettaglio />} />
                            <Route path="gare/:id/live" element={<GaraLive />} />
                            <Route path="risultato/:rrId" element={<CoachGaraRisultato />} />
                            <Route path="altro" element={<CoachAltro />} />
                            <Route path="altro/team" element={<CoachTeam />} />
                            <Route path="altro/leaderboard" element={<CoachLeaderboard />} />
                            <Route path="altro/graduatorie" element={<CoachGraduatorie />} />
                            <Route path="altro/tempi-limite" element={<CoachTempiLimite />} />
                            <Route path="altro/record" element={<CoachRecordItaliani />} />
                            <Route path="altro/record-societari" element={<CoachRecordSocietari />} />
                            <Route path="altro/notifiche" element={<CoachNotifiche />} />
                            <Route path="altro/profilo" element={<CoachProfilo />} />
                            <Route path="altro/confronto" element={<CoachConfronto />} />
                            {/* sotto-route da aggiungere man mano */}
                        </Route>
                    </Route>

                    <Route element={<ProtectedRoute role="atleta" />}>
                        <Route path="/atleta" element={<AtletaLayout />}>
                            <Route index element={<AthleteHome />} />

                            {/* Allenamenti — team-only contenuto, libero vede JoinTeamCard via TeamGuard */}
                            <Route path="allenamenti" element={<AtletaAllenamentiLista />} />
                            <Route path="allenamenti/:id" element={<AtletaAllenamentoDettaglio />} />

                            {/* Gare — pagina madre con tab PB/Mie gare/Calendario (PRD 27) */}
                            <Route path="gare" element={<AtletaGare />} />
                            <Route path="gara/:id" element={<AtletaGaraDettaglio />} />
                            <Route path="risultato/:id" element={<AtletaRisultatoDettaglio />} />

                            {/* Analisi — pagina madre con tab Personale/Splits/Confronti (PRD 29) */}
                            <Route path="analisi" element={<AtletaAnalisi />} />

                            {/* Altro hub — Squadra spostata qui (DA1=D) */}
                            <Route path="altro" element={<AtletaAltro />} />
                            <Route path="altro/squadra" element={<Squadra />} />
                            <Route path="altro/squadra/atleta/:id" element={<AtletaSquadraDettaglio />} />
                            <Route path="altro/squadra/gara/:id" element={<SquadraGara />} />
                            <Route path="altro/leaderboard" element={<AtletaLeaderboard />} />
                            <Route path="altro/graduatorie" element={<AtletaGraduatorie />} />
                            <Route path="altro/tempi-limite" element={<AtletaTempiLimite />} />
                            <Route path="altro/record" element={<RecordItaliani />} />
                            <Route path="altro/notifiche" element={<AtletaNotifiche />} />
                            <Route path="altro/profilo" element={<AtletaProfilo />} />
                            <Route path="altro/record-societari" element={<RecordSocietari />} />

                            {/* Redirect legacy paths per non rompere bookmark/PWA salvate */}
                            <Route
                                path="altro/confronti"
                                element={<Navigate to="/atleta/analisi?tab=confronto" replace />}
                            />
                            <Route path="altro/andamento" element={<Navigate to="/atleta/altro" replace />} />
                            <Route path="altro/staffette" element={<Navigate to="/atleta/altro" replace />} />
                            <Route path="tempi" element={<Navigate to="/atleta/gare" replace />} />
                            <Route path="squadra" element={<Navigate to="/atleta/altro/squadra" replace />} />
                            <Route path="squadra/atleta/:id" element={<RedirectSquadraAtleta />} />
                            <Route path="squadra/gara/:id" element={<RedirectSquadraGara />} />
                        </Route>
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
}
